import {
  FeedCard as SharedFeedCard,
  ModalRoot,
  useFeedCardWidthTier,
} from '@socialchorus/shared-ui-components';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useFeatureFlag from '../../../common/use-feature-flag';
import { usePatronSelector } from '../../../common/use-patron-selector';
import { timeAgoOrDate } from '../../../lib/date-formatter';
import { contentSelectors } from '../../../models/content';
import { Content } from '../../../models/content/types';
import { Feature } from '../../../models/features/features';
import { programSelectors } from '../../../models/program';
import { ViewLikesModal } from '../../v2/content-actions/view-likes-modal/view-likes-modal';
import { FeedCardActions, useHasFeedCardActions } from './feed-card-actions';

/**
 * Inputs for the card's reactions-and-views summary row, shared by every
 * composition that shows one so the totals can't drift between cards.
 */
export function useFeedCardSummary(contentId: number) {
  const { t } = useTranslation();

  //controls minor layout changes associated with the reaction types deployment.
  const expandedReactionTypesDeployed = useFeatureFlag(
    Feature.EXPANDED_REACTION_TYPES_DEPLOY
  );
  //controls the reaction types feature
  const expandedReactionTypesEnabled = usePatronSelector((state) =>
    programSelectors.getExpandedReactionTypesEnabled(state)
  );

  const reactions = usePatronSelector((s) =>
    contentSelectors.getContentReactions(s, { contentId })
  );
  const reactionTotal = usePatronSelector((s) =>
    contentSelectors.getContentReactionTotal(s, { contentId })
  );
  const likeCount = usePatronSelector((s) =>
    contentSelectors.getContentLikeCount(s, { contentId })
  );
  const viewCount = usePatronSelector((s) =>
    contentSelectors.getContentViewCount(s, { contentId })
  );
  const viewsEnabled = usePatronSelector((s) =>
    programSelectors.getFeedCardViewsEnabled(s)
  );
  const publishedAt = usePatronSelector((s) =>
    contentSelectors.getContentPublishedAt(s, { contentId })
  );

  const additionalMetadata: string[] = [];
  if (!expandedReactionTypesDeployed) {
    additionalMetadata.push(publishedAt ? timeAgoOrDate(publishedAt) : '-');
  } else if (expandedReactionTypesEnabled) {
    additionalMetadata.push(
      t('content.reactions_with_count', { count: reactionTotal })
    );
  } else {
    additionalMetadata.push(
      t('content.likes_with_count', { count: likeCount })
    );
  }
  if (viewsEnabled) {
    const viewWord = t('content.view', { count: viewCount });
    const viewDisplayText =
      t('compact_number', { value: viewCount }) + ` ${viewWord}`; //this is invalid for translation, the correct order of word and number is different per language. revisit when views is officially made into a feature.

    additionalMetadata.push(viewDisplayText);
  }

  const top3ReactionTypes = reactions
    .filter((r) => r.count > 0)
    .sort((a, b) => b.count - a.count)
    .slice(0, 3)
    .map((reaction) => reaction.emoji);

  return {
    additionalMetadata,
    reactedEmojis: expandedReactionTypesEnabled ? top3ReactionTypes : undefined,
    displayLikeIcon:
      !!expandedReactionTypesDeployed && !expandedReactionTypesEnabled,
    // Only allow clicking to open the reactions modal when there is at least
    // one like (legacy) or one reaction (expanded), otherwise keep it static.
    isClickable:
      !!expandedReactionTypesDeployed &&
      (expandedReactionTypesEnabled ? reactionTotal > 0 : likeCount > 0),
    ariaLabel: expandedReactionTypesEnabled
      ? t('content.view_reactions_action')
      : t('content.view_likes_action'),
  };
}

export type MinimalCardEngagementProps = {
  content: Content;
  analyticsData: IAnalyticsData;
};

/**
 * The engagement row of a `-minimal` card, sized to the card's own measured
 * width rather than the viewport. Must be rendered inside `FeedCard.Root`,
 * which is where that measurement becomes readable.
 *
 * Reaction and comment hold their slots at every width, so only the auxiliary
 * actions and the overflow trigger compete for what is left: two of them fit
 * on a wide card and none on a narrow one, except that a single leftover stays
 * inline instead of becoming an overflow menu of one.
 */
export function MinimalCardActions({
  content,
  analyticsData,
}: MinimalCardEngagementProps) {
  const widthTier = useFeedCardWidthTier();

  return (
    <FeedCardActions
      content={content}
      analyticsData={analyticsData}
      maxVisibleActions={widthTier === 'narrow' ? 2 : 4}
      compactCounts
      constrainWidth
    />
  );
}

/**
 * The engagement row of the full-density card, which serves both the minimal
 * mobile layouts and the non-minimal ones. The tier reads `wide` outside a
 * minimal layout, so the non-minimal cards keep their full action count without
 * a branch here; only the width-yielding and count-abbreviating treatments need
 * gating, since they would truncate labels that fit at desktop widths.
 */
export function FullDensityCardActions({
  content,
  analyticsData,
  isMinimal,
}: MinimalCardEngagementProps & { isMinimal: boolean }) {
  const widthTier = useFeedCardWidthTier();

  return (
    <FeedCardActions
      content={content}
      analyticsData={analyticsData}
      maxVisibleActions={widthTier === 'narrow' ? 2 : 3}
      compactCounts={isMinimal}
      constrainWidth={isMinimal}
    />
  );
}

/** The summary row and engagement row a `-minimal` card stacks in its footer. */
export function MinimalCardEngagement({
  content,
  analyticsData,
}: MinimalCardEngagementProps) {
  const reactionsDisabled = useFeatureFlag(Feature.REACTIONS_DISABLED);
  const hasActions = useHasFeedCardActions(content);
  const [showReactions, setShowReactions] = useState(false);
  const {
    additionalMetadata,
    reactedEmojis,
    displayLikeIcon,
    isClickable,
    ariaLabel,
  } = useFeedCardSummary(content.id);

  return (
    <>
      <SharedFeedCard.Footer>
        {isClickable ? (
          <SharedFeedCard.Metadata
            additionalMetadata={additionalMetadata}
            displayLikeIcon={displayLikeIcon}
            reactedEmojis={reactedEmojis}
            onClick={() => setShowReactions(true)}
            ariaLabel={ariaLabel}
          />
        ) : (
          <SharedFeedCard.Metadata
            additionalMetadata={additionalMetadata}
            displayLikeIcon={displayLikeIcon}
            reactedEmojis={reactedEmojis}
          />
        )}
        {!reactionsDisabled && hasActions ? (
          <MinimalCardActions content={content} analyticsData={analyticsData} />
        ) : null}
      </SharedFeedCard.Footer>
      <ModalRoot
        open={showReactions}
        onOpenChange={(setOpen) => setShowReactions(setOpen)}
      >
        {/* Mounted only while open — the modal fetches the reactor list as
            soon as it renders, and a feed holds many of these cards. */}
        {showReactions ? <ViewLikesModal contentId={content.id} /> : null}
      </ModalRoot>
    </>
  );
}
